import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adsService } from '../../api/adsService';
import { mapAdSlot } from '../../utils/adMapper';

/**
 * Async thunk to fetch ad slots
 * GET /api/ads?active_only=true
 */
export const fetchAds = createAsyncThunk(
  'ads/fetchAds',
  async (activeOnly = true, { rejectWithValue }) => {
    try {
      const data = await adsService.getAds(activeOnly);
      if (data?.success === false) {
        return rejectWithValue(data.error || 'Failed to fetch ads');
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch ads');
    }
  },
  {
    // Several ad slots mount on the same page; fetch only once
    condition: (_, { getState }) => {
      const { status } = getState().ads;
      return status === 'idle' || status === 'failed';
    },
  }
);

const initialState = {
  // Normalized ad slots keyed by slot id ('leaderboard', 'infeed', 'incontent'); see utils/adMapper
  slots: {},
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

export const adsSlice = createSlice({
  name: 'ads',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAds.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAds.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const data = action.payload?.data;
        const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        state.slots = list.reduce((acc, ad) => {
          const slot = mapAdSlot(ad);
          if (slot) acc[slot.id] = slot;
          return acc;
        }, {});
      })
      .addCase(fetchAds.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to load ads';
      });
  },
});

export const selectAdSlot = (state, slotId) => state.ads.slots[slotId];

const hasSomethingToShow = (slot) => Boolean(slot && slot.enabled && (slot.imageUrl || slot.hasContent));

// First slot in `slotIds` with something to show, so a page can prefer a dedicated
// slot and fall back to a shared one
export const selectFirstAdSlot = (state, slotIds) =>
  slotIds.map((id) => state.ads.slots[id]).find(hasSomethingToShow);

export default adsSlice.reducer;
